(function () {
  'use strict';

  const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
  const DAYS_IN_YEAR = 365;

  const MONTHS_PT = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  /** Soma meses preservando o dia; encurta quando o mês de destino é mais curto. */
  function addMonths(date, months) {
    if (!months) return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(date.getDate(), lastDay));
    return target;
  }

  function formatDate(date) {
    try {
      const locale = document.documentElement.lang || 'pt-BR';
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    } catch (error) {
      return date.getDate() + ' de ' + MONTHS_PT[date.getMonth()];
    }
  }

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const formatted = window.Shopify.formatMoney(cents, window.money_format);
      return formatted.includes('R$') ? formatted : 'R$ ' + formatted;
    }
    return 'R$ ' + (cents / 100).toFixed(2).replace('.', ',');
  }

  /**
   * Monta a assinatura: escolhe produtos e frequência, compara o custo avulso
   * (com frete) contra o do clube, e envia cada item ao carrinho como a variante
   * da frequência escolhida (data-frequencies, gerado por dm-club-frequency-variants).
   */
  class ClubBuilder {
    constructor(root) {
      this.root = root;
      this.inline = root.dataset.layout === 'inline';
      this.qty = {};
      this.lastFocus = null;
      this.dialog = root.querySelector('.dmc-builder__dialog');
      this.summary = root.querySelector('[data-dmc-summary]');
      this.itemsList = root.querySelector('[data-dmc-items]');
      this.freqLabel = root.querySelector('[data-dmc-freq-label]');
      this.errorBoxes = Array.from(root.querySelectorAll('[data-dmc-error]'));
      this.submitBtn = root.querySelector('[data-dmc-submit]');
      this.products = Array.from(root.querySelectorAll('[data-dmc-product]'));

      this.confirm = root.querySelector('[data-dmc-confirm]');
      this.confirmLastFocus = null;

      this.upsell = root.querySelector('[data-dmc-upsell]');
      this.recapThumbs = root.querySelector('[data-dmc-recap-thumbs]');
      this.upsellGift = root.querySelector('[data-dmc-upsell-gift]');
      this.checkoutBtn = root.querySelector('[data-dmc-checkout]');
      this.upsellLastFocus = null;

      this.schedule = root.querySelector('[data-dmc-schedule]');
      this.finishBtn = root.querySelector('[data-dmc-finish]');
      this.acceptBox = root.querySelector('[data-dmc-schedule-accept]');
      this.scheduleLastFocus = null;

      this.bundle = root.querySelector('[data-dmc-bundle]');
      this.bundleNext = root.querySelector('[data-dmc-bundle-next]');
      this.bundleLastFocus = null;

      this.rootUrl = (root.dataset.rootUrl || '/').replace(/\/+$/, '') + '/';
      this.discountCode = root.dataset.discountCode || '';
      this.clearCart = root.dataset.clearCart === 'true';
      this.attributeName = root.dataset.attributeName || 'Frequência';
      this.shipping = parseInt(root.dataset.shipping, 10) || 0;
      this.offLabel = root.dataset.offLabel || 'OFF';
      this.giftThreshold = parseInt(root.dataset.giftThreshold, 10) || 0;
      this.giftVariant = root.dataset.giftVariant || '';

      this.bind();
      this.syncFrequency();
      this.applyEntryParams();
    }

    /**
     * Chegada pelo cartão de assinatura da PDP (button-assinatura.liquid):
     * ?frequencia=monthly|bimonthly marca a frequência (30 ou 60 dias entre entregas)
     * e ?produto=<id> já adiciona o produto, se ele estiver no montador.
     */
    applyEntryParams() {
      const params = new URLSearchParams(window.location.search);
      const days = { monthly: '30', bimonthly: '60' }[params.get('frequencia')];
      const productId = params.get('produto');
      if (!days && !productId) return;

      const radio = days && this.root.querySelector('[data-dmc-frequency][data-days="' + days + '"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        this.syncFrequency();
      }

      const card = productId && this.products.find((el) => el.dataset.productId === productId);
      if (card && !this.qty[card.dataset.variantId]) this.setQty(card.dataset.variantId, 1);

      if (!radio && !card) return;
      if (this.inline) this.root.scrollIntoView({ block: 'start' });
      else this.open();
    }

    bind() {
      if (this.inline) {
        document.addEventListener('click', (event) => this.selectPlan(event));
      }

      // Inline o montador ja esta na pagina: abrir/fechar, prender foco e o Escape
      // nao se aplicam — o Escape chegaria em close() e esconderia a secao inteira.
      if (!this.inline) {
        const anchor = '#' + this.root.dataset.anchor;
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('a[href*="#"], [data-dm-club-builder-open]');
          if (!trigger) return;
          const isAnchor = trigger.hasAttribute('data-dm-club-builder-open') || trigger.hash === anchor;
          if (!isAnchor) return;
          event.preventDefault();
          this.open();
        });

        this.root.querySelectorAll('[data-dmc-close]').forEach((el) => {
          el.addEventListener('click', () => this.close());
        });

        document.addEventListener('keydown', (event) => {
          if (this.root.hidden) return;
          if (event.key === 'Escape') this.close();
          if (event.key === 'Tab') this.trapFocus(event);
        });
      }

      this.root.querySelectorAll('[data-dmc-frequency]').forEach((input) => {
        input.addEventListener('change', () => this.syncFrequency());
      });

      const next = this.root.querySelector('[data-dmc-next]');
      if (next) next.addEventListener('click', () => this.goToFrequency());

      this.root.querySelectorAll('[data-dmc-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.dmcTab));
      });

      // Os cards do compre junto entram em products (para somar no carrinho),
      // mas nao tem stepper: quem adiciona la e o botao da propria oferta.
      this.products.forEach((item) => {
        const id = item.dataset.variantId;
        const inc = item.querySelector('[data-dmc-inc]');
        const dec = item.querySelector('[data-dmc-dec]');
        if (inc) inc.addEventListener('click', () => this.setQty(id, (this.qty[id] || 0) + 1));
        if (dec) dec.addEventListener('click', () => this.setQty(id, (this.qty[id] || 0) - 1));
      });

      // Com o popup de confirmacao no ar, o botao final apenas o abre; quem fecha
      // o pedido e o botao de dentro dele.
      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', () => {
          if (this.confirm) this.openConfirm();
          else this.advanceFrom('confirm', this.submitBtn);
        });
      }

      if (this.confirm) this.bindConfirm();
      if (this.upsell) this.bindUpsell();
      if (this.schedule) this.bindSchedule();
      if (this.bundle) this.bindBundle();
    }

    /** Seleciona a frequência do plano sem reiniciar os produtos já escolhidos. */
    selectPlan(event) {
      if (!this.root.isConnected || event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('a[data-dmc-plan-days]');
      if (!trigger || trigger.hasAttribute('download') ||
          (trigger.target && trigger.target !== '_self')) return;

      const url = new URL(trigger.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname ||
          url.search !== window.location.search || url.hash !== '#' + this.root.id) return;

      const days = Number(trigger.dataset.dmcPlanDays);
      if (!Number.isInteger(days) || days <= 0) return;
      const radio = Array.from(this.root.querySelectorAll('[data-dmc-frequency]'))
        .find((input) => Number(input.dataset.days) === days);
      const step = this.root.querySelector('[data-dmc-step="1"]');
      if (!radio || !step) return;

      event.preventDefault();
      radio.checked = true;
      this.syncFrequency();
      step.hidden = false;
      const heading = step.querySelector('.dmc-builder__step-title');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      this.root.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    bindBundle() {
      // Sem stepper, o botao da oferta funciona como liga/desliga: clicar de novo
      // tira o item, e so quem fecha o pedido sao "prosseguir" e "nao quero".
      this.bundle.querySelectorAll('[data-dmc-bundle-add]').forEach((button) => {
        button.addEventListener('click', () => {
          const card = button.closest('[data-dmc-product]');
          if (!card) return;
          const id = card.dataset.variantId;
          this.setQty(id, this.qty[id] ? 0 : 1);
        });
      });

      const skip = this.bundle.querySelector('[data-dmc-bundle-skip]');
      if (skip) skip.addEventListener('click', () => this.submit(skip));

      if (this.bundleNext) {
        this.bundleNext.addEventListener('click', () => this.submit(this.bundleNext));
      }

      // Fechar pelo overlay ou Escape volta para a pagina sem fechar o pedido;
      // quem segue para o checkout sao os dois botoes acima.
      this.bundle.querySelectorAll('[data-dmc-bundle-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeBundle());
      });

      document.addEventListener('keydown', (event) => {
        if (this.bundle.hidden) return;
        if (event.key === 'Escape') this.closeBundle();
        if (event.key === 'Tab') this.trapFocus(event, this.bundle);
      });
    }

    /** "Prosseguir" só aparece quando algum item da própria oferta está escolhido. */
    renderBundle() {
      if (!this.bundle) return;

      let chosen = false;
      this.bundle.querySelectorAll('[data-dmc-product]').forEach((card) => {
        const on = Boolean(this.qty[card.dataset.variantId]);
        if (on) chosen = true;

        const button = card.querySelector('[data-dmc-bundle-add]');
        if (!button) return;
        button.textContent = on ? button.dataset.labelAdded : button.dataset.labelAdd;
        button.classList.toggle('dmc-btn--ghost', on);
        button.setAttribute('aria-pressed', String(on));
      });

      if (this.bundleNext) this.bundleNext.hidden = !chosen;
    }

    openBundle() {
      if (this.confirm) this.confirm.hidden = true;
      if (this.upsell) this.upsell.hidden = true;
      if (this.schedule) this.schedule.hidden = true;

      this.bundleLastFocus = this.scheduleLastFocus || this.upsellLastFocus || this.confirmLastFocus || document.activeElement;
      this.bundle.hidden = false;
      document.body.classList.add('dmc-builder-open');

      const focusTarget = this.bundle.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    closeBundle() {
      this.bundle.hidden = true;
      document.body.classList.remove('dmc-builder-open');
      if (this.bundleLastFocus) this.bundleLastFocus.focus();
    }

    /**
     * Encadeia os popups finais na ordem confirmação → sugestões → prazos →
     * compre junto → checkout. Cada etapa é opcional: sem ela, o botão da etapa
     * anterior pula direto para a seguinte.
     */
    advanceFrom(step, button) {
      const order = ['confirm', 'upsell', 'schedule', 'bundle'];
      const from = order.indexOf(step);

      if (from < order.indexOf('upsell') && this.upsell) {
        this.openUpsell();
        return;
      }
      if (from < order.indexOf('schedule') && this.schedule) {
        this.openSchedule();
        return;
      }
      if (from < order.indexOf('bundle') && this.bundle) {
        this.openBundle();
        return;
      }
      this.submit(button);
    }

    bindConfirm() {
      this.confirm.querySelectorAll('[data-dmc-confirm-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeConfirm());
      });

      this.confirm.querySelectorAll('[data-dmc-confirm-frequency]').forEach((input) => {
        input.addEventListener('change', () => this.applyConfirmFrequency(input.value));
      });

      const next = this.confirm.querySelector('[data-dmc-confirm-next]');
      if (next) next.addEventListener('click', () => this.advanceFrom('confirm', next));

      document.addEventListener('keydown', (event) => {
        if (this.confirm.hidden) return;
        if (event.key === 'Escape') this.closeConfirm();
        if (event.key === 'Tab') this.trapFocus(event, this.confirm);
      });
    }

    bindUpsell() {
      this.upsell.querySelectorAll('[data-dmc-upsell-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeUpsell());
      });

      this.upsell.querySelectorAll('[data-dmc-upsell-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectUpsellTab(tab.dataset.dmcUpsellTab));
      });

      const toggle = this.upsell.querySelector('[data-dmc-recap-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.toggleRecap());

      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', () => this.advanceFrom('upsell', this.checkoutBtn));
      }

      document.addEventListener('keydown', (event) => {
        if (this.upsell.hidden) return;
        if (event.key === 'Escape') this.closeUpsell();
        if (event.key === 'Tab') this.trapFocus(event, this.upsell);
      });
    }

    bindSchedule() {
      this.schedule.querySelectorAll('[data-dmc-schedule-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeSchedule());
      });

      if (this.acceptBox && this.finishBtn) {
        this.acceptBox.addEventListener('change', () => {
          this.finishBtn.disabled = !this.acceptBox.checked;
        });
      }

      if (this.finishBtn) {
        this.finishBtn.addEventListener('click', () => this.advanceFrom('schedule', this.finishBtn));
      }

      document.addEventListener('keydown', (event) => {
        if (this.schedule.hidden) return;
        if (event.key === 'Escape') this.closeSchedule();
        if (event.key === 'Tab') this.trapFocus(event, this.schedule);
      });
    }

    openSchedule() {
      if (this.confirm) this.confirm.hidden = true;
      if (this.upsell) this.upsell.hidden = true;

      this.renderSchedule();
      this.scheduleLastFocus = this.upsellLastFocus || this.confirmLastFocus || document.activeElement;
      this.schedule.hidden = false;
      document.body.classList.add('dmc-builder-open');

      const focusTarget = this.schedule.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    closeSchedule() {
      this.schedule.hidden = true;
      document.body.classList.remove('dmc-builder-open');
      if (this.scheduleLastFocus) this.scheduleLastFocus.focus();
    }

    /**
     * Datas das primeiras entregas. Frequências em múltiplos de 30 dias andam de
     * mês em mês para preservar o dia escolhido (dia 15 → todo dia 15); as demais
     * somam dias corridos.
     */
    renderSchedule() {
      const input = this.frequency;
      const days = input ? parseInt(input.dataset.days, 10) || 30 : 30;
      const months = days % 30 === 0 ? days / 30 : 0;
      const today = new Date();

      this.schedule.querySelectorAll('[data-dmc-schedule-date]').forEach((el) => {
        const index = parseInt(el.dataset.dmcScheduleDate, 10) || 0;
        const date = months
          ? addMonths(today, months * index)
          : new Date(today.getFullYear(), today.getMonth(), today.getDate() + days * index);
        el.textContent = formatDate(date);
      });
    }

    openUpsell() {
      this.setRecapExpanded(true);
      if (this.confirm) this.confirm.hidden = true;
      this.syncFrequency();
      this.upsellLastFocus = this.confirmLastFocus || document.activeElement;
      this.upsell.hidden = false;
      document.body.classList.add('dmc-builder-open');

      const focusTarget = this.upsell.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    closeUpsell() {
      this.upsell.hidden = true;
      document.body.classList.remove('dmc-builder-open');
      if (this.upsellLastFocus) this.upsellLastFocus.focus();
    }

    selectUpsellTab(id) {
      this.upsell.querySelectorAll('[data-dmc-upsell-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.dmcUpsellTab === id));
      });
      this.upsell.querySelectorAll('[data-dmc-upsell-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.dmcUpsellPanel !== id;
      });
    }

    setRecapExpanded(open) {
      const toggle = this.upsell.querySelector('[data-dmc-recap-toggle]');
      if (!toggle) return;
      // getElementById em vez de seletor: o id da secao traz "--" e "__".
      const body = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!body) return;
      toggle.setAttribute('aria-expanded', String(open));
      body.hidden = !open;
    }

    toggleRecap() {
      const toggle = this.upsell.querySelector('[data-dmc-recap-toggle]');
      if (toggle) this.setRecapExpanded(toggle.getAttribute('aria-expanded') !== 'true');
    }

    openConfirm() {
      const current = this.frequency;
      if (current) {
        const mirror = this.confirm.querySelector('[data-dmc-confirm-frequency][value="' + current.value + '"]');
        if (mirror) mirror.checked = true;
      }

      this.confirmLastFocus = document.activeElement;
      this.confirm.hidden = false;
      document.body.classList.add('dmc-builder-open');

      const focusTarget = this.confirm.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    closeConfirm() {
      this.confirm.hidden = true;
      document.body.classList.remove('dmc-builder-open');
      if (this.confirmLastFocus) this.confirmLastFocus.focus();
    }

    /** Espelha a frequência escolhida no popup de volta nos radios da página. */
    applyConfirmFrequency(blockId) {
      const target = this.root.querySelector('[data-dmc-frequency][value="' + blockId + '"]');
      if (!target) return;
      target.checked = true;
      this.syncFrequency();
    }

    open() {
      this.lastFocus = document.activeElement;
      this.root.hidden = false;
      document.body.classList.add('dmc-builder-open');
      const focusTarget = this.root.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    close() {
      this.root.hidden = true;
      document.body.classList.remove('dmc-builder-open');
      if (this.lastFocus) this.lastFocus.focus();
    }

    trapFocus(event, container) {
      const scope = container || this.dialog;
      const items = Array.from(scope.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    get frequency() {
      return this.root.querySelector('[data-dmc-frequency]:checked');
    }

    /** Variante do produto na frequência escolhida: { variant, price }. */
    frequencyVariant(item) {
      if (!item.dmcFrequencies) {
        try {
          item.dmcFrequencies = JSON.parse(item.dataset.frequencies || '{}');
        } catch (error) {
          item.dmcFrequencies = {};
        }
      }
      return this.frequency ? item.dmcFrequencies[this.frequency.value] : undefined;
    }

    /** Quantas entregas cabem em um ano na frequência escolhida (30 dias → 12). */
    get deliveriesPerYear() {
      const input = this.frequency;
      const days = input ? parseInt(input.dataset.days, 10) : 0;
      if (!days || days < 1) return 0;
      return Math.floor(DAYS_IN_YEAR / days);
    }

    /** Usa o preço da variante da frequência enviada ao carrinho. */
    subPrice(item) {
      const match = this.frequencyVariant(item);
      return match ? match.price : parseInt(item.dataset.price, 10);
    }

    syncFrequency() {
      if (this.freqLabel) {
        this.freqLabel.textContent = this.frequency ? this.frequency.dataset.label : '';
      }

      const badge = this.frequency ? this.frequency.dataset.discount + '% ' + this.offLabel : '';
      this.root.querySelectorAll('[data-dmc-upsell-badge]').forEach((el) => {
        el.textContent = badge;
        el.hidden = !badge;
      });

      this.products.forEach((item) => {
        const price = item.querySelector('[data-dmc-upsell-price]');
        if (!price) return;
        const full = item.querySelector('[data-dmc-upsell-full]');
        const regular = parseInt(item.dataset.price, 10);
        const subscription = this.subPrice(item);
        price.textContent = money(subscription);
        if (full) {
          full.textContent = money(regular);
          full.hidden = subscription >= regular;
        }
      });

      this.render();
    }

    goToFrequency() {
      this.setStep(2);
      this.summary.hidden = false;
      this.render();
    }

    setStep(step) {
      this.root.querySelectorAll('[data-dmc-step]').forEach((el) => {
        el.hidden = el.dataset.dmcStep !== String(step);
      });
    }

    selectTab(id) {
      this.root.querySelectorAll('[data-dmc-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.dmcTab === id));
      });
      this.root.querySelectorAll('[data-dmc-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.dmcPanel !== id;
      });
    }

    setQty(id, value) {
      const next = Math.max(0, value);
      if (next === 0) delete this.qty[id];
      else this.qty[id] = next;

      // O mesmo produto pode estar na etapa 1 e nas sugestoes: os dois cards
      // representam a mesma linha do carrinho e precisam mostrar a mesma quantidade.
      this.products
        .filter((el) => el.dataset.variantId === id)
        .forEach((el) => {
          const output = el.querySelector('[data-dmc-qty]');
          if (output) output.textContent = String(next);
          const addButton = el.querySelector('[data-dmc-add-label]');
          if (addButton) {
            addButton.textContent = next > 0 ? '+' : addButton.dataset.dmcAddLabel;
            addButton.setAttribute('aria-label', next > 0
              ? addButton.dataset.dmcIncreaseLabel
              : addButton.dataset.dmcAddLabel + ' ' + el.dataset.title);
          }
          el.classList.toggle('is-selected', next > 0);
        });

      this.render();
    }

    /** Uma entrada por variante escolhida, mesmo que ela apareça em mais de uma tela. */
    cartLines() {
      const seen = new Set();
      const lines = [];
      this.products.forEach((item) => {
        const id = item.dataset.variantId;
        if (seen.has(id)) return;
        seen.add(id);
        const quantity = this.qty[id];
        if (!quantity) return;
        lines.push({
          id: id,
          quantity: quantity,
          title: item.dataset.title,
          image: item.dataset.image,
          price: parseInt(item.dataset.price, 10),
          sub: this.subPrice(item),
          match: this.frequencyVariant(item)
        });
      });
      return lines;
    }

    totals() {
      let full = 0;
      let sub = 0;
      this.cartLines().forEach((line) => {
        full += line.price * line.quantity;
        sub += line.sub * line.quantity;
      });
      return { full: full, sub: sub };
    }

    render() {
      const t = this.totals();
      const isEmpty = t.full === 0;
      // O frete so entra na comparacao quando ha carrinho: sem produto, comparar
      // "R$ 0 avulso" com "R$ 0 no clube" mostraria uma economia que nao existe.
      const single = isEmpty ? 0 : t.full + this.shipping;
      const savings = single - t.sub;

      this.root.classList.toggle('is-empty', isEmpty);
      this.fillItems(this.itemsList);
      this.renderRecap();
      this.renderBundle();

      const set = (selector, text) => {
        const el = this.root.querySelector(selector);
        if (el) el.textContent = text;
      };
      set('[data-dmc-single]', money(single));
      set('[data-dmc-club]', money(t.sub));
      set('[data-dmc-savings]', money(savings));
      set('[data-dmc-annual]', money(savings * this.deliveriesPerYear));

      const annualRow = this.root.querySelector('[data-dmc-annual-row]');
      if (annualRow) annualRow.hidden = this.deliveriesPerYear === 0;

      if (this.submitBtn) this.submitBtn.disabled = isEmpty;
      this.renderGift(t.sub);
      this.hideError();
    }

    /** Preenche uma lista com "Nx Produto" para cada item escolhido. */
    fillItems(list) {
      if (!list) return;
      list.textContent = '';
      this.cartLines().forEach((line) => {
        const li = document.createElement('li');
        li.textContent = line.quantity + 'x ' + line.title;
        list.appendChild(li);
      });
    }

    renderRecap() {
      if (!this.upsell) return;

      if (this.recapThumbs) {
        this.recapThumbs.textContent = '';
        this.cartLines().forEach((line) => {
          const li = document.createElement('li');
          li.className = 'dmc-upsell__thumb';

          if (line.image) {
            const img = document.createElement('img');
            img.src = line.image;
            img.alt = line.title;
            img.loading = 'lazy';
            li.appendChild(img);
          }

          const count = document.createElement('span');
          count.className = 'dmc-upsell__thumb-count';
          count.textContent = String(line.quantity);
          li.appendChild(count);

          this.recapThumbs.appendChild(li);
        });
      }

      const t = this.totals();
      const set = (selector, text) => {
        const el = this.upsell.querySelector(selector);
        if (el) el.textContent = text;
      };
      set('[data-dmc-recap-subtotal]', money(t.full));
      set('[data-dmc-recap-savings]', money(t.full - t.sub));
      set('[data-dmc-recap-total]', money(t.sub));

      if (this.upsellGift) {
        this.upsellGift.hidden = !this.giftThreshold || t.sub < this.giftThreshold;
      }
      if (this.checkoutBtn) this.checkoutBtn.disabled = t.full === 0;
    }

    renderGift(subCents) {
      const wrap = this.root.querySelector('[data-dmc-gift]');
      if (!wrap || !this.giftThreshold) return;
      const missing = Math.max(0, this.giftThreshold - subCents);
      const pct = Math.min(100, (subCents / this.giftThreshold) * 100);
      const settings = this.root.dataset;
      const text = missing > 0
        ? (settings.giftLockedText || '').replace('[valor]', money(missing))
        : settings.giftUnlockedText || '';
      wrap.querySelector('[data-dmc-gift-text]').textContent = text;
      wrap.querySelector('[data-dmc-gift-fill]').style.width = pct + '%';
      wrap.classList.toggle('is-unlocked', missing === 0);
    }

    showError(message) {
      this.errorBoxes.forEach((el) => {
        el.textContent = message;
        el.hidden = false;
      });
    }

    hideError() {
      this.errorBoxes.forEach((el) => {
        el.hidden = true;
      });
    }

    buildItems() {
      // A linha é identificada pela variante do card; ao carrinho vai a da frequência.
      // Sem propriedade por item: a variante já diz a frequência, e uma propriedade
      // impediria a Shopify de juntar a linha com a mesma variante vinda de outra página.
      const items = this.cartLines().map((line) => ({
        id: Number(line.match ? line.match.variant : line.id),
        quantity: line.quantity
      }));
      const t = this.totals();
      if (this.giftVariant && this.giftThreshold && t.sub >= this.giftThreshold) {
        items.push({ id: Number(this.giftVariant), quantity: 1 });
      }
      return items;
    }

    async submit(button) {
      const trigger = button || this.submitBtn;
      const items = this.buildItems();
      if (!items.length) {
        this.showError(this.root.dataset.emptyError || 'Escolha pelo menos um produto.');
        return;
      }

      trigger.disabled = true;
      trigger.classList.add('is-loading');

      try {
        if (this.clearCart) {
          await fetch(this.rootUrl + 'cart/clear.js', { method: 'POST' });
        }

        const label = this.frequency ? this.frequency.dataset.label : '';
        const add = await fetch(this.rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items, attributes: { [this.attributeName]: label } })
        });
        if (!add.ok) throw new Error('cart/add failed');

        window.location.href = this.checkoutUrl();
      } catch (error) {
        trigger.disabled = false;
        trigger.classList.remove('is-loading');
        this.showError(this.root.dataset.genericError || 'Tente novamente.');
      }
    }

    checkoutUrl() {
      const base = this.rootUrl;
      if (!this.discountCode) return base + 'checkout';
      return base + 'discount/' + encodeURIComponent(this.discountCode) + '?redirect=' + encodeURIComponent(base + 'checkout');
    }
  }

  function init() {
    document.querySelectorAll('[data-dm-club-builder]').forEach((root) => {
      if (!root.dataset.dmcReady) {
        root.dataset.dmcReady = 'true';
        new ClubBuilder(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
